import sortBy from 'lodash/sortBy';

import { Z_CALENDAR_EVENT_TYPE } from '../constants';
import { capTourEventsPerSlot } from './capTourEventsPerSlot';
import { intervalsOverlap, isCancelledTourEvent } from './tourEventPredicates';

const AVAILABILITY_EVENT_OFFSET = 4;
const AVAILABILITY_EVENT_WIDTH = 100 - AVAILABILITY_EVENT_OFFSET * 2;
const AVAILABILITY_EVENT_WIDTH_WITH_OVERLAP = 80 - AVAILABILITY_EVENT_OFFSET * 2;
const TOUR_EVENT_WIDTH = (2 / 3) * 100;
const TOUR_EVENT_PADDING = 4;
const TOUR_EVENT_OFFSET = (1 / 3) * 100 - TOUR_EVENT_PADDING;
// Cancelled tours live in a left lane. The first sits just inside the availability block's left
// edge; each additional cancelled tour that shares the slot steps right by CANCELLED_CASCADE_STEP
// so every tile keeps an exposed, hoverable strip instead of hiding behind the one on top of it.
const CANCELLED_CASCADE_BASE_OFFSET = AVAILABILITY_EVENT_OFFSET + TOUR_EVENT_PADDING;
const CANCELLED_CASCADE_STEP = 7;
// Never let a cascaded cancelled tour reach the active-tour lane — keep it at least one step left.
const CANCELLED_CASCADE_MAX_OFFSET = TOUR_EVENT_OFFSET - CANCELLED_CASCADE_STEP;
const EVENT_HEIGHT_PADDING = 0.25; // Remove 0.25% from event height to create space between events

// For events sharing a start, availability renders first (so it stays the overlap container),
// then cancelled tours, so an overlapping active tour renders last in the DOM and paints on top.
const renderRank = (event) => {
  if (event.data.eventType === Z_CALENDAR_EVENT_TYPE.AVAILABILITY) return 0;
  return isCancelledTourEvent(event.data) ? 1 : 2;
};

// Greedy interval-graph coloring over cancelled tours: each is assigned the smallest cascade slot
// not taken by an already-placed cancelled tour it overlaps in time. Overlapping tours therefore
// always get distinct slots (they fan out), while non-overlapping ones reuse slot 0.
const assignCancelledCascadeSlots = (eventsInRenderOrder) => {
  const placed = [];
  for (const event of eventsInRenderOrder) {
    if (!isCancelledTourEvent(event.data)) continue;
    const takenSlots = new Set(
      placed.filter((other) => intervalsOverlap(event, other)).map((other) => other.cascadeSlot),
    );
    let slot = 0;
    while (takenSlots.has(slot)) slot += 1;
    event.cascadeSlot = slot;
    placed.push(event);
  }
};

// File copied from react big calendar overlap.js
// https://github.com/jquense/react-big-calendar/blob/86b26cd8a20ec11e953999650c10d59f4170f1ce/src/utils/layout-algorithms/overlap.js
// Changes only made in get width, get xOffset, the sortByRender ordering, and the cancelled cascade
class Event {
  constructor(data, { accessors, slotMetrics }) {
    const { start, startDate, end, endDate, top, height } = slotMetrics.getRange(
      accessors.start(data),
      accessors.end(data),
    );

    this.start = start;
    this.end = end;
    this.startMs = +startDate;
    this.endMs = +endDate;
    this.top = top;
    this.height = height - EVENT_HEIGHT_PADDING;
    this.data = data;
  }

  // START OF CHANGES DIFFERENT FROM SOURCE ALGORITHM
  get width() {
    // Cancelled tours are the same width as active tours; they just cascade in a left lane.
    if (isCancelledTourEvent(this.data)) return TOUR_EVENT_WIDTH;

    // Event that has rows is an availability event
    if (this.rows) {
      // If rows.length > 0, the event is being overlapped
      if (this.rows.length > 0) {
        return AVAILABILITY_EVENT_WIDTH_WITH_OVERLAP;
      }
      return AVAILABILITY_EVENT_WIDTH;
    }

    // Anything else is a tour event
    return TOUR_EVENT_WIDTH;
  }

  get xOffset() {
    // A cancelled tour that shares a slot with another tour cascades in the left lane, one step
    // per overlapping cancelled tour, so each tile keeps an exposed, hoverable strip. Capped so
    // it never reaches the active-tour lane. Overlap shows up in the grouping as either being a
    // leaf (`this.row` set) or a row with leaves (`this.leaves.length > 0`); a lone cancelled tour
    // is a row with empty leaves, so it falls through to the normal tour lane like an active tour.
    if (isCancelledTourEvent(this.data) && (this.row || this.leaves?.length > 0)) {
      return Math.min(
        CANCELLED_CASCADE_BASE_OFFSET + (this.cascadeSlot ?? 0) * CANCELLED_CASCADE_STEP,
        CANCELLED_CASCADE_MAX_OFFSET,
      );
    }

    // Event that has rows is an availability event
    // Availability events have no offset.
    if (this.rows) return AVAILABILITY_EVENT_OFFSET;

    // Tour events are 2/3 of the width of the row so offset must be 1/3
    return TOUR_EVENT_OFFSET;
  }
  // END OF CHANGES DIFFERENT FROM SOURCE ALGORITHM
}

/**
 * Return true if event a and b is considered to be on the same row.
 */
function onSameRow(a, b, minimumStartDifference) {
  return (
    // Occupies the same start slot.
    Math.abs(b.start - a.start) < minimumStartDifference ||
    // A's start slot overlaps with b's end slot.
    (b.start > a.start && b.start < a.end)
  );
}

function sortByRender(events) {
  // CHANGED FROM SOURCE ALGORITHM: renderRank breaks same-start ties (see comment above)
  const sortedByTime = sortBy(events, ['startMs', renderRank, (e) => -e.endMs]);

  const sorted = [];
  while (sortedByTime.length > 0) {
    const event = sortedByTime.shift();
    sorted.push(event);

    for (let i = 0; i < sortedByTime.length; i++) {
      const test = sortedByTime[i];

      // Still inside this event, look for next.
      if (event.endMs > test.startMs) continue;

      // We've found the first event of the next event group.
      // If that event is not right next to our current event, we have to
      // move it here.
      if (i > 0) {
        const event = sortedByTime.splice(i, 1)[0];
        sorted.push(event);
      }

      // We've already found the next event group, so stop looking.
      break;
    }
  }

  return sorted;
}

export default function getStyledEvents({ events, minimumStartDifference, slotMetrics, accessors }) {
  // Cap the tour tiles per slot before laying anything out, so an over-cap tile is never positioned
  // (and the cascade only ever sees <= 3 cancelled tours, keeping their slots distinct).
  const cappedEvents = capTourEventsPerSlot(events, accessors);

  // Create proxy events and order them so that we don't have
  // to fiddle with z-indexes.
  const proxies = cappedEvents.map((event) => new Event(event, { slotMetrics, accessors }));
  const eventsInRenderOrder = sortByRender(proxies);
  assignCancelledCascadeSlots(eventsInRenderOrder);

  // Group overlapping events, while keeping order.
  // Every event is always one of: container, row or leaf.
  // Containers can contain rows, and rows can contain leaves.
  const containerEvents = [];
  for (let i = 0; i < eventsInRenderOrder.length; i++) {
    const event = eventsInRenderOrder[i];

    // Check if this event can go into a container event.
    const container = containerEvents.find(
      (c) => c.end > event.start || Math.abs(event.start - c.start) < minimumStartDifference,
    );

    // Couldn't find a container — that means this event is a container.
    if (!container) {
      event.rows = [];
      containerEvents.push(event);
      continue;
    }

    // Found a container for the event.
    event.container = container;

    // Check if the event can be placed in an existing row.
    // Start looking from behind.
    let row = null;
    for (let j = container.rows.length - 1; !row && j >= 0; j--) {
      if (onSameRow(container.rows[j], event, minimumStartDifference)) {
        row = container.rows[j];
      }
    }

    if (row) {
      // Found a row, so add it.
      row.leaves.push(event);
      event.row = row;
    } else {
      // Couldn't find a row – that means this event is a row.
      event.leaves = [];
      container.rows.push(event);
    }
  }

  // Return the original events, along with their styles.
  return eventsInRenderOrder.map((event) => ({
    event: event.data,
    style: {
      top: event.top,
      height: event.height,
      width: event.width,
      xOffset: Math.max(0, event.xOffset),
    },
  }));
}
